import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { format } from 'date-fns';
import { 
  Table, 
  TableHeader, 
  TableBody, 
  TableRow, 
  TableHead, 
  TableCell 
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import { useToast } from '@/hooks/use-toast';
import { ChevronUp, ChevronDown, MapPin, Image, TrendingUp, Target, CheckCircle, XCircle, Clock, Eye, Heart } from 'lucide-react';
import { favoriteService } from '@/services/favoriteService';
import { useAuth } from '@/context/AuthContext';
import { Spinner } from '@/components/ui/spinner';
import { supabase } from '@/integrations/supabase/client';
import PageLayout from '@/components/layout/PageLayout';
import { HistoryFilters, TargetTypeFilter, StatusFilter } from '@/components/history/HistoryFilters';
import { LocationThumbnail } from '@/components/history/LocationThumbnail';
import { PredictionAccuracyCard } from '@/components/history/PredictionAccuracyCard';

interface TargetHistoryItem {
  id: string;
  target_code: string;
  target_type: 'location' | 'image' | 'prediction';
  thumbnail_url: string;
  created_at: string;
  resolved_at: string | null;
  session_id: string;
  resolution_status: 'unresolved' | 'correct' | 'incorrect' | 'completed' | 'in_progress';
  is_favorite: boolean;
}

type SortField = 'created_at' | 'target_type' | 'resolution_status' | 'resolved_at';
type SortDirection = 'asc' | 'desc';

const TARGETS_PER_PAGE = 20;

import { formatTargetCode } from '@/utils/targetCodeUtils';

// Status sort order for consistent sorting
const STATUS_SORT_ORDER: Record<string, number> = {
  'in_progress': 0,
  'unresolved': 1,
  'correct': 2,
  'completed': 3,
  'incorrect': 4,
};

const History = () => {
  const [allTargets, setAllTargets] = useState<TargetHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [sortField, setSortField] = useState<SortField>('created_at');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [pageInput, setPageInput] = useState('');
  const [selectedTargets, setSelectedTargets] = useState<Set<string>>(new Set());
  const [isTogglingFavorites, setIsTogglingFavorites] = useState(false);
  
  // Initialize filters from URL params
  const getInitialTypeFilters = (): Set<TargetTypeFilter> => {
    const urlType = searchParams.get('type');
    if (urlType) {
      const types = urlType.split(',').filter(t => 
        ['location', 'image', 'prediction'].includes(t)
      ) as TargetTypeFilter[];
      if (types.length > 0) {
        return new Set(types);
      }
    }
    return new Set(['location', 'image', 'prediction']);
  };

  const getInitialStatusFilters = (): Set<StatusFilter> => {
    const urlStatus = searchParams.get('status');
    if (urlStatus) {
      const statuses = urlStatus.split(',').filter(s => 
        ['unresolved', 'correct', 'incorrect', 'completed', 'in_progress'].includes(s)
      ) as StatusFilter[];
      if (statuses.length > 0) {
        return new Set(statuses);
      }
    }
    return new Set(['unresolved', 'correct', 'incorrect', 'completed', 'in_progress']);
  };

  // Filter states - initialize from URL params
  const [typeFilters, setTypeFilters] = useState<Set<TargetTypeFilter>>(getInitialTypeFilters);
  const [statusFilters, setStatusFilters] = useState<Set<StatusFilter>>(getInitialStatusFilters);
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(searchParams.get('favorites') === 'true');
  
  const { user, session, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  // Get current page from URL params, default to 1
  const currentPage = parseInt(searchParams.get('page') || '1', 10);

  // Apply filters and sorting
  const filteredAndSortedTargets = React.useMemo(() => {
    // First filter
    let filtered = allTargets.filter(target => {
      const typeMatch = typeFilters.has(target.target_type);
      const statusMatch = statusFilters.has(target.resolution_status);
      const favoriteMatch = !showFavoritesOnly || target.is_favorite;
      return typeMatch && statusMatch && favoriteMatch;
    });

    // Then sort
    filtered.sort((a, b) => {
      let comparison = 0;
      
      switch (sortField) {
        case 'created_at':
          comparison = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
          break;
        case 'resolved_at':
          // Null values go to the end when sorting
          if (!a.resolved_at && !b.resolved_at) comparison = 0;
          else if (!a.resolved_at) comparison = sortDirection === 'desc' ? 1 : -1;
          else if (!b.resolved_at) comparison = sortDirection === 'desc' ? -1 : 1;
          else comparison = new Date(a.resolved_at).getTime() - new Date(b.resolved_at).getTime();
          break;
        case 'target_type':
          comparison = a.target_type.localeCompare(b.target_type);
          break;
        case 'resolution_status':
          comparison = (STATUS_SORT_ORDER[a.resolution_status] ?? 99) - (STATUS_SORT_ORDER[b.resolution_status] ?? 99);
          break;
      }
      
      return sortDirection === 'desc' ? -comparison : comparison;
    });

    return filtered;
  }, [allTargets, typeFilters, statusFilters, showFavoritesOnly, sortField, sortDirection]);

  const totalTargets = filteredAndSortedTargets.length;
  const totalPages = Math.ceil(totalTargets / TARGETS_PER_PAGE);
  
  // Paginated targets
  const targets = React.useMemo(() => {
    const from = (currentPage - 1) * TARGETS_PER_PAGE;
    const to = from + TARGETS_PER_PAGE;
    return filteredAndSortedTargets.slice(from, to);
  }, [filteredAndSortedTargets, currentPage]);

  // Update URL with current filters and page
  const updateSearchParams = (page: number, types?: Set<TargetTypeFilter>, statuses?: Set<StatusFilter>, favorites?: boolean) => {
    const validPage = Math.max(1, Math.min(page, totalPages || 1));
    const params: Record<string, string> = { page: validPage.toString() };
    
    // Add type filter to URL if not all types selected
    const currentTypes = types ?? typeFilters;
    const allTypes = new Set(['location', 'image', 'prediction']);
    if (currentTypes.size < 3) {
      params.type = Array.from(currentTypes).join(',');
    }
    
    // Add status filter to URL if not all statuses selected
    const currentStatuses = statuses ?? statusFilters;
    const allStatuses = new Set(['unresolved', 'correct', 'incorrect', 'completed', 'in_progress']);
    if (currentStatuses.size < 5) {
      params.status = Array.from(currentStatuses).join(',');
    }
    
    // Add favorites filter to URL
    const currentFavorites = favorites ?? showFavoritesOnly;
    if (currentFavorites) {
      params.favorites = 'true';
    }
    
    setSearchParams(params);
  };

  // Update page in URL
  const setCurrentPage = (page: number) => {
    updateSearchParams(page);
  };

  // Sync filters to URL when they change - reset to page 1
  useEffect(() => {
    updateSearchParams(1, typeFilters, statusFilters, showFavoritesOnly);
  }, [typeFilters, statusFilters, showFavoritesOnly]);

  // Reset to page 1 when sort changes
  useEffect(() => {
    if (currentPage !== 1) {
      updateSearchParams(1);
    }
  }, [sortField, sortDirection]);

  // Reset to page 1 when filters change and page is out of bounds
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      updateSearchParams(1);
    }
  }, [totalPages, currentPage]);

  // Load targets
  useEffect(() => {
    const loadData = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        // Fetch regular targets with revealed_at and is_favorite
        const { data: regularTargets, error: regularError } = await supabase
          .from('targets')
          .select('id, target_code, target_type, target_url, created_at, session_id, revealed_at, is_favorite')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false });

        if (regularError) throw regularError;

        // Fetch prediction targets with event close_time for resolution date and is_favorite
        const { data: predictionTargets, error: predictionError } = await supabase
          .from('prediction_targets')
          .select(`
            id, 
            target_code, 
            yes_image_url, 
            no_image_url, 
            created_at, 
            session_id,
            revealed_at,
            is_favorite,
            prediction_events!inner (
              close_time
            )
          `)
          .eq('user_id', user.id)
          .order('created_at', { ascending: false });

        if (predictionError) throw predictionError;

        // EXPLICIT QUERY: Fetch user predictions by target_id (not session_id) for accuracy
        const predictionTargetIds = (predictionTargets || []).map(t => t.id);
        let userPredictionsByTargetId = new Map<string, { user_selected_outcome: string; is_correct: boolean | null }>();
        
        if (predictionTargetIds.length > 0) {
          const { data: userPredictions, error: upError } = await supabase
            .from('user_predictions')
            .select('target_id, user_selected_outcome, is_correct')
            .eq('user_id', user.id)
            .in('target_id', predictionTargetIds);
          
          if (upError) {
            if (import.meta.env.DEV) console.error('⚠️ History: Failed to fetch user_predictions:', upError);
          } else {
            for (const up of userPredictions || []) {
              userPredictionsByTargetId.set(up.target_id, {
                user_selected_outcome: up.user_selected_outcome,
                is_correct: up.is_correct
              });
            }
            if (import.meta.env.DEV) console.log('✅ History: Loaded user predictions for', userPredictionsByTargetId.size, 'targets');
          }
        }

        // Fallback: infer selection from messages if user_predictions is missing
        const parsePredictionSelection = (content: string, targetCode?: string): 'YES' | 'NO' | null => {
          const raw = (content || '').toString();
          const c = raw.toLowerCase();
          if (!(c.includes('my prediction selection') || c.includes('prediction selection') || c.includes('your selection'))) {
            return null;
          }
          // If target code is provided, prefer messages that mention it (new format)
          if (targetCode) {
            const cleanCode = targetCode.replace(/-/g, '');
            const formattedCode = cleanCode.length === 8 ? `${cleanCode.substring(0,4)}-${cleanCode.substring(4)}` : targetCode;
            const mentionsCode = raw.toUpperCase().includes(formattedCode.toUpperCase()) || raw.toUpperCase().includes(cleanCode.toUpperCase());
            if (mentionsCode) {
              if (/(option\s*a\b)/i.test(raw)) return 'YES';
              if (/(option\s*b\b)/i.test(raw)) return 'NO';
            }
          }
          // Legacy: any selection message
          if (/(option\s*a\b)/i.test(raw)) return 'YES';
          if (/(option\s*b\b)/i.test(raw)) return 'NO';
          return null;
        };

        // Build fallback map for targets without user_predictions
        const targetsNeedingInference = (predictionTargets || []).filter(t => !userPredictionsByTargetId.has(t.id));
        let inferredSelectionByTargetId = new Map<string, 'YES' | 'NO'>();
        
        if (targetsNeedingInference.length > 0) {
          const sessionIds = Array.from(new Set(targetsNeedingInference.map(t => t.session_id).filter(Boolean)));
          
          if (sessionIds.length > 0) {
            const { data: selectionMessages, error: selectionMsgError } = await supabase
              .from('messages')
              .select('session_id, content, created_at')
              .eq('user_id', user.id)
              .eq('role', 'user')
              .in('session_id', sessionIds)
              .ilike('content', '%prediction%selection%')
              .order('created_at', { ascending: false });

            if (!selectionMsgError && selectionMessages) {
              // Map messages to their target by session and target code
              for (const t of targetsNeedingInference) {
                const messagesForSession = selectionMessages.filter(m => m.session_id === t.session_id);
                for (const m of messagesForSession) {
                  const sel = parsePredictionSelection(m.content || '', t.target_code);
                  if (sel) {
                    inferredSelectionByTargetId.set(t.id, sel);
                    break;
                  }
                }
              }
            }
          }
        }

        // Transform and combine targets
        const transformedRegular: TargetHistoryItem[] = (regularTargets || []).map(t => {
          // For non-prediction targets: "Completed" if revealed, "In Progress" if not
          const resolutionStatus = t.revealed_at ? 'completed' as const : 'in_progress' as const;
          
          return {
            id: t.id,
            target_code: t.target_code,
            target_type: t.target_type as 'location' | 'image',
            thumbnail_url: t.target_url, // Will be processed by LocationThumbnail for location types
            created_at: t.created_at,
            resolved_at: t.revealed_at,
            session_id: t.session_id,
            resolution_status: resolutionStatus,
            is_favorite: t.is_favorite ?? false
          };
        });

        const transformedPrediction: TargetHistoryItem[] = (predictionTargets || []).map(t => {
          // Get the user's selected outcome - prefer database, fall back to transcript inference
          const dbPrediction = userPredictionsByTargetId.get(t.id);
          const inferredSelection = inferredSelectionByTargetId.get(t.id);
          
          const selectedOutcome = (dbPrediction?.user_selected_outcome || inferredSelection || '')
            .toString()
            .toUpperCase();
          const isCorrect = dbPrediction?.is_correct ?? null;
          
          // Use the image matching the user's selected outcome
          const thumbnailUrl = selectedOutcome === 'YES' ? t.yes_image_url : 
                              selectedOutcome === 'NO' ? t.no_image_url : 
                              t.yes_image_url; // Default to yes image if no selection recorded
          
          // Determine resolution status for prediction targets
          let resolutionStatus: 'unresolved' | 'correct' | 'incorrect' = 'unresolved';
          if (isCorrect === true) {
            resolutionStatus = 'correct';
          } else if (isCorrect === false) {
            resolutionStatus = 'incorrect';
          }
          
          // Use event close_time for predictions (when the event resolves)
          const eventCloseTime = (t as any).prediction_events?.close_time;
          
          return {
            id: t.id,
            target_code: t.target_code,
            target_type: 'prediction' as const,
            thumbnail_url: thumbnailUrl,
            created_at: t.created_at,
            resolved_at: eventCloseTime || null,
            session_id: t.session_id,
            resolution_status: resolutionStatus,
            is_favorite: t.is_favorite ?? false
          };
        });

        // Combine all targets (sorting is done in useMemo)
        setAllTargets([...transformedRegular, ...transformedPrediction]);

      } catch (error) {
        if (import.meta.env.DEV) console.error('❌ History: Failed to load data:', error);
        setError('Failed to load target history');
        toast({
          title: "Error",
          description: "Failed to load target history. Please try again.",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    if (!authLoading) {
      loadData();
    }
  }, [user, authLoading, toast]);

  // If still loading auth
  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spinner className="h-8 w-8 text-primary" />
        <p className="ml-2">Loading...</p>
      </div>
    );
  }

  // If not authenticated
  if (!user) {
    navigate('/');
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p>Redirecting to login...</p>
      </div>
    );
  }

  const handleTargetClick = async (target: TargetHistoryItem) => {
    try {
      // Navigate to historic target view with target ID and current page for back navigation
      navigate(`/?target=${target.id}&type=${target.target_type}&page=${currentPage}`);
      
      toast({
        title: "Target Loaded",
        description: `Loaded target ${formatTargetCode(target.target_code)}`,
      });
    } catch (error) {
      if (import.meta.env.DEV) console.error('❌ History: Error loading target:', error);
      toast({
        title: "Error",
        description: "Failed to load the selected target. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const handlePageInputSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const pageNum = parseInt(pageInput, 10);
    if (!isNaN(pageNum) && pageNum >= 1 && pageNum <= totalPages) {
      setCurrentPage(pageNum);
      setPageInput('');
    }
  };

  // Selection helpers
  const handleSelectTarget = (targetId: string, checked: boolean) => {
    setSelectedTargets(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(targetId);
      } else {
        newSet.delete(targetId);
      }
      return newSet;
    });
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedTargets(new Set(targets.map(t => t.id)));
    } else {
      setSelectedTargets(new Set());
    }
  };

  const handleToggleSingleFavorite = async (target: TargetHistoryItem, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent row click
    setIsTogglingFavorites(true);
    
    const result = await favoriteService.toggleFavorite(target.id, target.target_type, target.is_favorite);
    
    if (result.success) {
      setAllTargets(prev => prev.map(t => 
        t.id === target.id ? { ...t, is_favorite: result.isFavorite } : t
      ));
      toast({
        title: result.isFavorite ? "Added to favorites" : "Removed from favorites",
      });
    } else {
      toast({
        title: "Failed to update favorite",
        description: result.error,
        variant: "destructive",
      });
    }
    
    setIsTogglingFavorites(false);
  };

  const handleBulkFavorite = async (setAsFavorite: boolean) => {
    if (selectedTargets.size === 0) return;
    
    setIsTogglingFavorites(true);
    
    const targetsToUpdate = allTargets
      .filter(t => selectedTargets.has(t.id))
      .map(t => ({ id: t.id, targetType: t.target_type }));
    
    const result = await favoriteService.bulkSetFavorite(targetsToUpdate, setAsFavorite);
    
    if (result.success) {
      setAllTargets(prev => prev.map(t => 
        selectedTargets.has(t.id) ? { ...t, is_favorite: setAsFavorite } : t
      ));
      setSelectedTargets(new Set());
      toast({
        title: setAsFavorite ? "Added to favorites" : "Removed from favorites",
        description: `${targetsToUpdate.length} target${targetsToUpdate.length > 1 ? 's' : ''} updated`,
      });
    } else {
      toast({
        title: "Failed to update favorites",
        description: result.error,
        variant: "destructive",
      });
    }
    
    setIsTogglingFavorites(false);
  };

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'MMM d, yyyy');
    } catch (error) {
      return 'Unknown';
    }
  };

  const formatTime = (dateString: string) => {
    try {
      return format(new Date(dateString), 'h:mm a');
    } catch (error) {
      return '';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'location':
        return <MapPin className="h-4 w-4" />;
      case 'image':
        return <Image className="h-4 w-4" />;
      case 'prediction':
        return <TrendingUp className="h-4 w-4" />;
      default:
        return <Target className="h-4 w-4" />;
    }
  };

  const getTypeBadgeVariant = (type: string) => {
    switch (type) {
      case 'location':
        return 'bg-blue-500/20 text-blue-600 border-blue-500/30';
      case 'image':
        return 'bg-green-500/20 text-green-600 border-green-500/30';
      case 'prediction':
        return 'bg-purple-500/20 text-purple-600 border-purple-500/30';
      default:
        return '';
    }
  };

  const getResolutionBadge = (status: string, targetType: string) => {
    // For prediction targets, show correct/incorrect/unresolved
    if (targetType === 'prediction') {
      switch (status) {
        case 'correct':
          return (
            <Badge variant="outline" className="bg-emerald-500/20 text-emerald-600 border-emerald-500/30">
              <CheckCircle className="h-3 w-3 mr-1" />
              Correct
            </Badge>
          );
        case 'incorrect':
          return (
            <Badge variant="outline" className="bg-red-500/20 text-red-600 border-red-500/30">
              <XCircle className="h-3 w-3 mr-1" />
              Incorrect
            </Badge>
          );
        default:
          return (
            <Badge variant="outline" className="bg-amber-500/20 text-amber-600 border-amber-500/30">
              <Clock className="h-3 w-3 mr-1" />
              Unresolved
            </Badge>
          );
      }
    }
    
    // For image and location targets, show completed/in progress
    switch (status) {
      case 'completed':
        return (
          <Badge variant="outline" className="bg-emerald-500/20 text-emerald-600 border-emerald-500/30">
            <Eye className="h-3 w-3 mr-1" />
            Completed
          </Badge>
        );
      case 'in_progress':
      default:
        return (
          <Badge variant="outline" className="bg-blue-500/20 text-blue-600 border-blue-500/30">
            <Clock className="h-3 w-3 mr-1" />
            In Progress
          </Badge>
        );
    }
  };

  const SortIndicator = ({ field }: { field: SortField }) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? 
      <ChevronUp className="h-4 w-4 ml-1" /> : 
      <ChevronDown className="h-4 w-4 ml-1" />;
  };


  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages: (number | string)[] = [];
    const showEllipsis = totalPages > 7;
    
    if (showEllipsis) {
      pages.push(1);
      if (currentPage > 3) pages.push('ellipsis-start');
      for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
        pages.push(i);
      }
      if (currentPage < totalPages - 2) pages.push('ellipsis-end');
      if (totalPages > 1) pages.push(totalPages);
    } else {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    }

    return (
      <div className="mt-6 flex flex-col items-center gap-4">
        <Pagination>
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious 
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                className={currentPage === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
              />
            </PaginationItem>
            {pages.map((page, index) => (
              <PaginationItem key={index}>
                {page === 'ellipsis-start' || page === 'ellipsis-end' ? (
                  <PaginationEllipsis />
                ) : (
                  <PaginationLink
                    onClick={() => setCurrentPage(page as number)}
                    isActive={currentPage === page}
                    className="cursor-pointer"
                  >
                    {page}
                  </PaginationLink>
                )}
              </PaginationItem>
            ))}
            <PaginationItem>
              <PaginationNext 
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                className={currentPage === totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>

        {/* Page number input for 10+ pages */}
        {totalPages > 10 && (
          <form onSubmit={handlePageInputSubmit} className="flex items-center gap-2 text-sm text-muted-foreground">
            <span>Go to page:</span>
            <Input
              type="number"
              min={1}
              max={totalPages}
              value={pageInput}
              onChange={(e) => setPageInput(e.target.value)}
              placeholder={currentPage.toString()}
              className="w-20 h-8 text-center"
            />
            <span>of {totalPages}</span>
            <Button type="submit" variant="outline" size="sm" className="h-8">
              Go
            </Button>
          </form>
        )}
      </div>
    );
  };

  const renderThumbnail = (target: TargetHistoryItem) => {
    // For location targets, use the LocationThumbnail component with async API key loading
    if (target.target_type === 'location') {
      return (
        <LocationThumbnail 
          targetUrl={target.thumbnail_url} 
          targetCode={formatTargetCode(target.target_code)} 
        />
      );
    }
    
    // For other target types, use the thumbnail URL directly
    return (
      <div className="w-14 h-14 rounded-md overflow-hidden bg-muted flex items-center justify-center">
        {target.thumbnail_url ? (
          <img 
            src={target.thumbnail_url} 
            alt={`Target ${formatTargetCode(target.target_code)}`}
            loading="lazy"
            className="w-full h-full object-cover"
            onError={(e) => {
              const img = e.target as HTMLImageElement;
              img.style.display = 'none';
              img.parentElement?.classList.add('flex', 'items-center', 'justify-center');
              const icon = document.createElement('div');
              icon.textContent = target.target_type === 'prediction' ? '📊' : '📷';
              icon.className = 'text-2xl';
              img.parentElement?.appendChild(icon);
            }}
          />
        ) : (
          <span className="text-2xl">
            {target.target_type === 'prediction' ? '📊' : '📷'}
          </span>
        )}
      </div>
    );
  };

  return (
    <PageLayout title="Target History">
      <div className="max-w-6xl mx-auto py-12 px-6">
        <div className="mb-8 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <h1 className="text-4xl mb-2">History</h1>
            <p className="text-muted-foreground">Browse your previous targets and sessions</p>
          </div>
          <PredictionAccuracyCard session={session} />
        </div>
        
        {loading ? (
          <div className="text-center py-16">
            <Spinner className="h-12 w-12 text-primary mx-auto mb-4" />
            <p className="text-muted-foreground">Loading your target history...</p>
          </div>
        ) : error ? (
          <div className="text-center py-16">
            <Target className="h-16 w-16 text-red-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-red-700 mb-2">Error Loading History</h2>
            <p className="text-red-500 mb-6">{error}</p>
            <Button 
              onClick={() => window.location.reload()}
              className="bg-primary hover:bg-primary/90"
            >
              Try Again
            </Button>
          </div>
        ) : allTargets.length === 0 ? (
          <div className="text-center py-16">
            <Target className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-foreground mb-2">No targets yet</h2>
            <p className="text-muted-foreground mb-6">Generate your first target to start practicing</p>
            <Button 
              onClick={() => navigate('/')}
              className="bg-primary hover:bg-primary/90"
            >
              Start New Session
            </Button>
          </div>
        ) : (
          <>
            {/* Filters */}
            <HistoryFilters
              typeFilters={typeFilters}
              statusFilters={statusFilters}
              onTypeFilterChange={setTypeFilters}
              onStatusFilterChange={setStatusFilters}
              showFavoritesOnly={showFavoritesOnly}
              onFavoritesFilterChange={setShowFavoritesOnly}
            />

            {targets.length === 0 ? (
              <div className="text-center py-16 bg-card rounded-lg border border-border">
                <Target className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h2 className="text-lg font-semibold text-foreground mb-2">No matching targets</h2>
                <p className="text-muted-foreground">Try adjusting your filters</p>
              </div>
            ) : (
              <>
                {/* Bulk Actions Bar */}
                {selectedTargets.size > 0 && (
                  <div className="mb-4 p-3 bg-muted rounded-lg flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">
                      {selectedTargets.size} target{selectedTargets.size > 1 ? 's' : ''} selected
                    </span>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleBulkFavorite(true)}
                        disabled={isTogglingFavorites}
                      >
                        <Heart className="h-4 w-4 mr-1" />
                        Add to Favorites
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleBulkFavorite(false)}
                        disabled={isTogglingFavorites}
                      >
                        <Heart className="h-4 w-4 mr-1" />
                        Remove from Favorites
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setSelectedTargets(new Set())}
                      >
                        Clear Selection
                      </Button>
                    </div>
                  </div>
                )}

                <div className="bg-card rounded-lg shadow-sm border border-border overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/50">
                        <TableHead className="font-semibold text-primary py-4 w-12">
                          <Checkbox
                            checked={targets.length > 0 && selectedTargets.size === targets.length}
                            onCheckedChange={handleSelectAll}
                            aria-label="Select all"
                          />
                        </TableHead>
                        <TableHead className="font-semibold text-primary py-4 w-12">
                          <Heart className="h-4 w-4" />
                        </TableHead>
                        <TableHead className="font-semibold text-primary py-4 w-20">
                          <span className="sr-only">Thumbnail</span>
                        </TableHead>
                        <TableHead className="font-semibold text-primary py-4">
                          Code
                        </TableHead>
                        <TableHead 
                          className="font-semibold text-primary py-4 cursor-pointer hover:bg-muted"
                          onClick={() => handleSort('target_type')}
                        >
                          <div className="flex items-center">
                            Type
                            <SortIndicator field="target_type" />
                          </div>
                        </TableHead>
                        <TableHead 
                          className="font-semibold text-primary py-4 cursor-pointer hover:bg-muted"
                          onClick={() => handleSort('created_at')}
                        >
                          <div className="flex items-center">
                            Date
                            <SortIndicator field="created_at" />
                          </div>
                        </TableHead>
                        <TableHead className="font-semibold text-primary py-4">
                          Time
                        </TableHead>
                        <TableHead 
                          className="font-semibold text-primary py-4 cursor-pointer hover:bg-muted"
                          onClick={() => handleSort('resolved_at')}
                        >
                          <div className="flex items-center">
                            Closes
                            <SortIndicator field="resolved_at" />
                          </div>
                        </TableHead>
                        <TableHead 
                          className="font-semibold text-primary py-4 cursor-pointer hover:bg-muted"
                          onClick={() => handleSort('resolution_status')}
                        >
                          <div className="flex items-center">
                            Status
                            <SortIndicator field="resolution_status" />
                          </div>
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {targets.map((target) => (
                        <TableRow 
                          key={target.id} 
                          className={`cursor-pointer hover:bg-muted/50 transition-colors border-b border-border last:border-b-0 ${
                            selectedTargets.has(target.id) ? 'bg-muted/30' : ''
                          }`}
                          onClick={() => handleTargetClick(target)}
                        >
                          <TableCell className="py-3" onClick={(e) => e.stopPropagation()}>
                            <Checkbox
                              checked={selectedTargets.has(target.id)}
                              onCheckedChange={(checked) => handleSelectTarget(target.id, checked as boolean)}
                              aria-label={`Select target ${formatTargetCode(target.target_code)}`}
                            />
                          </TableCell>
                          <TableCell className="py-3">
                            <button
                              onClick={(e) => handleToggleSingleFavorite(target, e)}
                              disabled={isTogglingFavorites}
                              className={`p-1 rounded-full hover:bg-accent transition-colors ${
                                isTogglingFavorites ? 'opacity-50 cursor-wait' : ''
                              }`}
                              aria-label={target.is_favorite ? "Remove from favorites" : "Add to favorites"}
                            >
                              <Heart 
                                className={`h-4 w-4 transition-colors ${
                                  target.is_favorite 
                                    ? 'fill-primary text-primary' 
                                    : 'text-muted-foreground'
                                }`}
                              />
                            </button>
                          </TableCell>
                          <TableCell className="py-3">
                            {renderThumbnail(target)}
                          </TableCell>
                          <TableCell className="py-3">
                            <span className="font-mono text-sm font-medium text-foreground">
                              {formatTargetCode(target.target_code)}
                            </span>
                          </TableCell>
                          <TableCell className="py-3">
                            <Badge 
                              variant="outline" 
                              className={`capitalize ${getTypeBadgeVariant(target.target_type)}`}
                            >
                              <span className="mr-1">{getTypeIcon(target.target_type)}</span>
                              {target.target_type}
                            </Badge>
                          </TableCell>
                          <TableCell className="py-3 text-muted-foreground">
                            {formatDate(target.created_at)}
                          </TableCell>
                          <TableCell className="py-3 text-muted-foreground">
                            {formatTime(target.created_at)}
                          </TableCell>
                          <TableCell className="py-3 text-muted-foreground">
                            {target.resolved_at ? formatDate(target.resolved_at) : '—'}
                          </TableCell>
                          <TableCell className="py-3">
                            {getResolutionBadge(target.resolution_status, target.target_type)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </>
            )}
            
            {renderPagination()}
          </>
        )}
      </div>
    </PageLayout>
  );
};

export default History;
